import React, { useState, useEffect, useCallback } from 'react';

const DECISION_COLOR = {
  ALLOW:      { bg: '#e8f5e9', fg: '#1b5e20', border: '#66bb6a' },
  STEP_UP:    { bg: '#fff8e1', fg: '#8d6e00', border: '#ffb300' },
  HOLD:       { bg: '#fff3e0', fg: '#a0522d', border: '#f57c00' },
  QUARANTINE: { bg: '#fff3e0', fg: '#a0522d', border: '#ef6c00' },
  REVOKE:     { bg: '#fde7e9', fg: '#b71c1c', border: '#e53935' },
  FREEZE:     { bg: '#fde7e9', fg: '#b71c1c', border: '#c62828' },
  BLOCK:      { bg: '#fde7e9', fg: '#b71c1c', border: '#c62828' },
};

const SEVERITY_LABEL = { 1: 'INFO', 2: 'LOW', 3: 'MEDIUM', 4: 'HIGH', 5: 'CRITICAL' };

function DecisionBadge({ decision, severity }) {
  const c = DECISION_COLOR[decision] || { bg: 'var(--gold-glow)', fg: 'var(--gold)', border: 'var(--gold)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '3px 8px',
      borderRadius: 4, background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    }}>
      {decision}{severity ? ` · ${SEVERITY_LABEL[severity] || severity}` : ''}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '\u2014';
  const dt = typeof d === 'number' ? new Date(d) : new Date(d);
  return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SafetyCenter() {
  const [status, setStatus] = useState(null);
  const [cases, setCases] = useState([]);
  const [killLog, setKillLog] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [tab, setTab] = useState('overview');

  // Evaluator form
  const [ev, setEv] = useState({ amount: '1000', currency: 'USD', beneficiary: '', accountNo: '', receiverBIC: '', narrative: '' });
  const [evResult, setEvResult] = useState(null);

  // Neutralization forms
  const [freezeIban, setFreezeIban] = useState('');
  const [quarantineKey, setQuarantineKey] = useState('');
  const [lockKey, setLockKey] = useState('');
  const [wlKind, setWlKind] = useState('BIC');
  const [wlValue, setWlValue] = useState('');
  const [wlReason, setWlReason] = useState('');
  const [sensorType, setSensorType] = useState('login_failure');
  const [sensorData, setSensorData] = useState('');

  const refresh = useCallback(() => {
    fetch('/api/safety/status').then(r => r.json()).then(setStatus).catch(() => {});
    fetch('/api/safety/cases').then(r => r.json()).then(setCases).catch(() => {});
    fetch('/api/safety/kill-log?limit=100').then(r => r.json()).then(d => setKillLog([...d].reverse())).catch(() => {});
    fetch('/api/safety/evidence?limit=100').then(r => r.json()).then(d => setEvidence([...d].reverse())).catch(() => {});
    fetch('/api/safety/sensor?windowMs=3600000').then(r => r.json()).then(d => setSensors([...d].reverse())).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const post = async (path, body) => {
    const r = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
    return r.json();
  };
  const del = async (path, body) => {
    const r = await fetch(path, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
    return r.json();
  };

  const runEvaluate = async () => {
    const body = {
      amount: parseFloat(ev.amount) || 0,
      currency: ev.currency,
      beneficiary: ev.beneficiary || undefined,
      accountNo: ev.accountNo || undefined,
      receiverBIC: ev.receiverBIC || undefined,
      narrative: ev.narrative || undefined,
    };
    setEvResult(await post('/api/safety/evaluate', body));
  };

  const doFreeze = async () => { if (!freezeIban.trim()) return; await post('/api/safety/freeze', { iban: freezeIban.trim(), reason: 'manual freeze from Safety Center' }); setFreezeIban(''); refresh(); };
  const doUnfreeze = async (iban) => { await post('/api/safety/unfreeze', { iban, reason: 'manual unfreeze' }); refresh(); };
  const doQuarantine = async () => { if (!quarantineKey.trim()) return; await post('/api/safety/quarantine', { key: quarantineKey.trim(), reason: 'manual quarantine', durationMs: 24*3600000 }); setQuarantineKey(''); refresh(); };
  const doRelease = async (key) => { await post('/api/safety/release', { key, reason: 'manual release' }); refresh(); };
  const doLock = async () => { if (!lockKey.trim()) return; await post('/api/safety/lock-payee', { key: lockKey.trim(), reason: 'manual payee lock' }); setLockKey(''); refresh(); };
  const doUnlock = async (key) => { await post('/api/safety/unlock-payee', { key, reason: 'manual unlock' }); refresh(); };
  const doWlAdd = async () => { if (!wlValue.trim()) return; await post('/api/safety/watchlist', { kind: wlKind, value: wlValue.trim(), reason: wlReason || 'manual' }); setWlValue(''); setWlReason(''); refresh(); };
  const doWlRemove = async (kind, value) => { await del('/api/safety/watchlist', { kind, value }); refresh(); };
  const doSensor = async () => {
    let data = {};
    try { data = sensorData.trim() ? JSON.parse(sensorData) : {}; } catch { data = { note: sensorData }; }
    await post('/api/safety/sensor', { type: sensorType, data });
    setSensorData('');
    refresh();
  };
  const doCloseCase = async (id) => { await post(`/api/safety/cases/${id}/close`, { resolution: 'closed from Safety Center', operator: 'admin' }); refresh(); };
  const doManualScan = async () => { await post('/api/safety/scan'); refresh(); };

  const openCases = cases.filter(c => c.status === 'OPEN');
  const closedCases = cases.filter(c => c.status === 'CLOSED');

  return (
    <div className="gap-16">
      {/* Header + status tiles */}
      <div className="card">
        <div className="card-body" style={{ padding: '24px 32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--text3)', fontWeight: 700, marginBottom: 6 }}>
                BANK-GRADE FRAUD PROTECTION
              </div>
              <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: 22, color: 'var(--navy)', fontWeight: 700 }}>
                Safety Center \u2014 Regulated Core
              </div>
              <p style={{ color: 'var(--text2)', margin: '6px 0 0', fontSize: 12 }}>
                PDP gate \u00b7 sensor mesh \u00b7 kill layer (block/freeze/quarantine/revoke/hold) \u00b7 WORM evidence chain.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                borderRadius: 12, background: status?.scanner?.running ? '#e8f5e9' : '#fde7e9',
                color: status?.scanner?.running ? '#1b5e20' : '#b71c1c',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: status?.scanner?.running ? '#43a047' : '#e53935' }} />
                SCANNER {status?.scanner?.running ? 'RUNNING' : 'STOPPED'}
              </span>
              <button className="btn btn-outline btn-sm" onClick={doManualScan}>Scan Now</button>
              <button className="btn btn-outline btn-sm" onClick={refresh}>Refresh</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
            {[
              ['Evaluations', status?.stats?.totalEvaluations || 0],
              ['Blocks', status?.stats?.totalBlocks || 0],
              ['Holds', status?.stats?.totalHolds || 0],
              ['Freezes', status?.stats?.totalFreezes || 0],
              ['Open Cases', status?.counts?.openCases || 0],
              ['Evidence Records', status?.counts?.evidenceRecords || 0],
            ].map(([k, v]) => (
              <div key={k} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', background: 'var(--surface)' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--text3)', fontWeight: 700 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', fontFamily: "'IBM Plex Mono', monospace" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {['overview','evaluator','controls','cases','evidence','sensors'].map(t => (
          <div key={t} onClick={() => setTab(t)}
            style={{
              padding: '10px 18px', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
              color: tab === t ? 'var(--gold)' : 'var(--text2)',
              borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent',
              marginBottom: -1, textTransform: 'uppercase',
            }}>{t}</div>
        ))}
      </div>

      {/* ============ OVERVIEW ============ */}
      {tab === 'overview' && (
        <div className="grid2" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Active Controls</span></div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['Frozen Accounts', status?.counts?.frozenAccounts || 0],
                ['Quarantined Payouts', status?.counts?.quarantinedPayouts || 0],
                ['Locked Payees', status?.counts?.lockedPayees || 0],
                ['BIC Watchlist', status?.counts?.watchlistBIC || 0],
                ['IBAN Watchlist', status?.counts?.watchlistIBAN || 0],
                ['Sensor Events (buffered)', status?.counts?.sensorEvents || 0],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'6px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <span style={{ color:'var(--text2)' }}>{k}</span>
                  <span style={{ fontFamily:"'IBM Plex Mono', monospace", color:'var(--gold)', fontWeight:700 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Recent Case Activity</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {cases.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No cases yet.</div>
              ) : (
                <table>
                  <thead><tr><th>Case</th><th>Decision</th><th>Reason</th><th>Opened</th></tr></thead>
                  <tbody>
                    {cases.slice(0, 6).map(c => (
                      <tr key={c.caseId}>
                        <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11 }}>{c.caseId.slice(5, 20)}</td>
                        <td><DecisionBadge decision={c.decision} severity={c.severity} /></td>
                        <td style={{ fontSize:11 }}>{(c.reasons || [])[0] || '\u2014'}</td>
                        <td style={{ fontSize:11, color:'var(--text3)' }}>{fmtDate(c.openedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ EVALUATOR (dry-run PDP) ============ */}
      {tab === 'evaluator' && (
        <div className="grid2" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">PDP Dry-Run Evaluator</span></div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>
                Simulate a transaction through the Policy Decision Point. No funds move; evidence is still logged.
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <input className="form-input" value={ev.amount} onChange={e => setEv({ ...ev, amount: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <input className="form-input" value={ev.currency} onChange={e => setEv({ ...ev, currency: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Beneficiary</label>
                <input className="form-input" value={ev.beneficiary} onChange={e => setEv({ ...ev, beneficiary: e.target.value })} placeholder="Receiver name" />
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Account / IBAN</label>
                  <input className="form-input" value={ev.accountNo} onChange={e => setEv({ ...ev, accountNo: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Receiver BIC</label>
                  <input className="form-input" value={ev.receiverBIC} onChange={e => setEv({ ...ev, receiverBIC: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Narrative</label>
                <input className="form-input" value={ev.narrative} onChange={e => setEv({ ...ev, narrative: e.target.value })} placeholder="Payment description" />
              </div>
              <button className="btn btn-gold" onClick={runEvaluate}>Evaluate</button>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Decision</span></div>
            <div className="card-body">
              {!evResult ? (
                <div style={{ color: 'var(--text3)', fontSize: 12 }}>Run an evaluation to see the PDP decision and rule hits.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <DecisionBadge decision={evResult.decision} severity={evResult.severity} />
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                      {evResult.allowed ? 'Transaction would be allowed.' : 'Transaction would be stopped.'}
                    </span>
                  </div>
                  {(evResult.hits && evResult.hits.length > 0) ? (
                    <div>
                      <div style={{ fontSize: 10, letterSpacing:'0.12em', color:'var(--text3)', fontWeight:700, marginBottom:6 }}>RULE HITS</div>
                      {evResult.hits.map((h, i) => (
                        <div key={i} style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 12px', marginBottom: 6 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                            <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:'var(--gold)' }}>{h.rule}</span>
                            <DecisionBadge decision={h.decision} severity={h.severity} />
                          </div>
                          <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>{h.reason}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>No rules triggered.</div>
                  )}
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily:"'IBM Plex Mono', monospace" }}>
                    Evidence #{evResult.evidenceSeq} \u00b7 {(evResult.evidenceHash || '').slice(0, 24)}\u2026
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ CONTROLS ============ */}
      {tab === 'controls' && (
        <>
          <div className="grid2" style={{ gap: 16 }}>
            {/* Freeze */}
            <div className="card">
              <div className="card-header"><span className="card-title">Freeze Account</span></div>
              <div className="card-body" style={{ display:'flex', gap: 8 }}>
                <input className="form-input" value={freezeIban} onChange={e => setFreezeIban(e.target.value)} placeholder="e.g. IBAN" />
                <button className="btn btn-danger" onClick={doFreeze}>Freeze</button>
              </div>
            </div>
            {/* Quarantine */}
            <div className="card">
              <div className="card-header"><span className="card-title">Quarantine Payout (24h)</span></div>
              <div className="card-body" style={{ display:'flex', gap: 8 }}>
                <input className="form-input" value={quarantineKey} onChange={e => setQuarantineKey(e.target.value)} placeholder="Account IBAN or merchant id" />
                <button className="btn btn-danger" onClick={doQuarantine}>Quarantine</button>
              </div>
            </div>
            {/* Lock payee */}
            <div className="card">
              <div className="card-header"><span className="card-title">Lock Payee</span></div>
              <div className="card-body" style={{ display:'flex', gap: 8 }}>
                <input className="form-input" value={lockKey} onChange={e => setLockKey(e.target.value)} placeholder="Beneficiary IBAN / Account No" />
                <button className="btn btn-danger" onClick={doLock}>Lock</button>
              </div>
            </div>
            {/* Watchlist */}
            <div className="card">
              <div className="card-header"><span className="card-title">Add to Watchlist</span></div>
              <div className="card-body" style={{ display:'flex', flexDirection:'column', gap: 8 }}>
                <div style={{ display:'flex', gap: 8 }}>
                  <select className="form-input" style={{ maxWidth: 110 }} value={wlKind} onChange={e => setWlKind(e.target.value)}>
                    <option value="BIC">BIC</option>
                    <option value="IBAN">IBAN</option>
                  </select>
                  <input className="form-input" value={wlValue} onChange={e => setWlValue(e.target.value.toUpperCase())} placeholder={wlKind === 'BIC' ? 'e.g. BADBANKXXX' : 'IBAN'} />
                </div>
                <div style={{ display:'flex', gap: 8 }}>
                  <input className="form-input" value={wlReason} onChange={e => setWlReason(e.target.value)} placeholder="Reason (sanctions, fraud, etc.)" />
                  <button className="btn btn-danger" onClick={doWlAdd}>Add</button>
                </div>
              </div>
            </div>
          </div>

          {/* Active lists */}
          <div className="card">
            <div className="card-header"><span className="card-title">Kill Log (neutralization history)</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {killLog.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No neutralization actions yet.</div>
              ) : (
                <table>
                  <thead><tr><th>When</th><th>Action</th><th>Target</th><th>Reason</th><th>Operator</th><th>Evidence</th></tr></thead>
                  <tbody>
                    {killLog.slice(0, 50).map((k, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtDate(k.at)}</td>
                        <td><span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 11, color: k.action.includes('REVERSE') || k.action.startsWith('UN') || k.action.startsWith('RELEASE') ? '#1b5e20' : '#b71c1c', fontWeight:700 }}>{k.action}</span></td>
                        <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 11 }}>{k.target}</td>
                        <td style={{ fontSize: 11 }}>{k.reason || '\u2014'}</td>
                        <td style={{ fontSize: 11, color:'var(--text3)' }}>{k.operator || '\u2014'}</td>
                        <td style={{ fontSize: 11, color:'var(--text3)' }}>#{k.evidenceSeq}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Reverse actions */}
          <div className="grid2" style={{ gap: 16 }}>
            <ReversibleList title="Frozen Accounts" items={status?.frozenAccountsList || []} emptyCount={status?.counts?.frozenAccounts} emptyLabel="No frozen accounts" />
            <ReversibleList title="Quarantined Payouts" items={status?.quarantinesList || []} emptyCount={status?.counts?.quarantinedPayouts} emptyLabel="No quarantines" />
          </div>
        </>
      )}

      {/* ============ CASES ============ */}
      {tab === 'cases' && (
        <>
          <div className="card">
            <div className="card-header"><span className="card-title">Open Cases ({openCases.length})</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {openCases.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No open cases.</div>
              ) : (
                <table>
                  <thead><tr><th>Case ID</th><th>Decision</th><th>Reason</th><th>Amount</th><th>Beneficiary</th><th>Opened</th><th></th></tr></thead>
                  <tbody>
                    {openCases.map(c => (
                      <tr key={c.caseId}>
                        <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 10 }}>{c.caseId}</td>
                        <td><DecisionBadge decision={c.decision} severity={c.severity} /></td>
                        <td style={{ fontSize: 11 }}>{(c.reasons || []).join('; ')}</td>
                        <td style={{ fontSize: 11, fontFamily:"'IBM Plex Mono', monospace" }}>{c.context?.amountUSD != null ? `$${Number(c.context.amountUSD).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : '\u2014'}</td>
                        <td style={{ fontSize: 11 }}>{c.context?.beneficiary || '\u2014'}</td>
                        <td style={{ fontSize: 11, color:'var(--text3)' }}>{fmtDate(c.openedAt)}</td>
                        <td><button className="btn btn-outline btn-sm" onClick={() => doCloseCase(c.caseId)}>Close</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Closed Cases ({closedCases.length})</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {closedCases.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>None.</div>
              ) : (
                <table>
                  <thead><tr><th>Case ID</th><th>Decision</th><th>Resolution</th><th>Closed</th></tr></thead>
                  <tbody>
                    {closedCases.slice(0, 20).map(c => (
                      <tr key={c.caseId}>
                        <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 10 }}>{c.caseId}</td>
                        <td><DecisionBadge decision={c.decision} severity={c.severity} /></td>
                        <td style={{ fontSize: 11 }}>{c.resolution}</td>
                        <td style={{ fontSize: 11, color:'var(--text3)' }}>{fmtDate(c.closedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ============ EVIDENCE ============ */}
      {tab === 'evidence' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Evidence Fabric (hash-chained, append-only)</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            {evidence.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No evidence yet.</div>
            ) : (
              <table>
                <thead><tr><th>#</th><th>When</th><th>Kind</th><th>Summary</th><th>Hash</th></tr></thead>
                <tbody>
                  {evidence.slice(0, 80).map(r => (
                    <tr key={r.seq}>
                      <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 11 }}>{r.seq}</td>
                      <td style={{ fontSize: 11, color:'var(--text3)' }}>{fmtDate(r.at)}</td>
                      <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{r.kind}</td>
                      <td style={{ fontSize: 11, maxWidth: 420, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {summarize(r.payload)}
                      </td>
                      <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 10, color:'var(--text3)' }}>{(r.hash || '').slice(0, 16)}\u2026</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ============ SENSORS ============ */}
      {tab === 'sensors' && (
        <>
          <div className="card">
            <div className="card-header"><span className="card-title">Ingest Sensor Event</span></div>
            <div className="card-body">
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.5 }}>
                Feed telemetry into the mesh. ATO rule reacts to <code>login_failure</code>, <code>device_change</code>, <code>otp_anomaly</code>.
                Other useful types: <code>payout_change</code>, <code>refund_spike</code>, <code>checkout_tamper</code>, <code>bot_detected</code>.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="form-input" style={{ maxWidth: 180 }} value={sensorType} onChange={e => setSensorType(e.target.value)}>
                  <option>login_failure</option>
                  <option>device_change</option>
                  <option>otp_anomaly</option>
                  <option>payout_change</option>
                  <option>refund_spike</option>
                  <option>checkout_tamper</option>
                  <option>bot_detected</option>
                </select>
                <input className="form-input" value={sensorData} onChange={e => setSensorData(e.target.value)} placeholder='JSON data e.g. {"user":"demo","ip":"1.2.3.4"}' />
                <button className="btn btn-gold" onClick={doSensor}>Send</button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Recent Sensor Events (1h)</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {sensors.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No events in window.</div>
              ) : (
                <table>
                  <thead><tr><th>When</th><th>Type</th><th>Data</th></tr></thead>
                  <tbody>
                    {sensors.slice(0, 80).map(s => (
                      <tr key={s.id}>
                        <td style={{ fontSize: 11, color:'var(--text3)' }}>{fmtDate(s.at)}</td>
                        <td style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize: 11, color:'var(--gold)' }}>{s.type}</td>
                        <td style={{ fontSize: 11, fontFamily:"'IBM Plex Mono', monospace" }}>{JSON.stringify(s.data)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function summarize(p) {
  if (!p || typeof p !== 'object') return String(p || '');
  if (p.decision) return `${p.decision} \u00b7 sev ${p.severity || '?'} \u00b7 ${(p.hits || []).map(h => h.rule).join(', ') || 'no hits'}`;
  if (p.action) return `${p.action} \u00b7 ${p.iban || p.key || p.sessionId || ''} \u00b7 ${p.reason || ''}`;
  if (p.kind && p.value) return `${p.kind}=${p.value} ${p.reason ? '\u00b7 ' + p.reason : ''}`;
  if (p.caseId) return `${p.caseId}${p.resolution ? ' \u00b7 ' + p.resolution : ''}`;
  return JSON.stringify(p).slice(0, 140);
}

function ReversibleList({ title, items, emptyCount, emptyLabel }) {
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{title}</span></div>
      <div className="card-body">
        <div style={{ fontSize: 12, color:'var(--text3)' }}>
          {emptyCount ? `${emptyCount} active (see Kill Log for reversal actions)` : emptyLabel}
        </div>
      </div>
    </div>
  );
}
